import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient, { BASE_URL } from 'api/client';
import useAuthStore, { selectPrincipal } from 'stores/authStore';

function loginDestination(location) {
  const from = location.state?.from;
  return from ? { pathname: from.pathname, search: from.search, hash: from.hash } : '/console/dashboard';
}

export function useAuth() {
  const store = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const login = useCallback(
    async ({ email, password, tenantSlug } = {}) => {
      const response = await apiClient.post('/api/v1/auth/login/', {
        email,
        password,
        ...(tenantSlug ? { tenant_slug: tenantSlug } : {}),
      }, { skipAuth: true });

      if (response.data.mfa_required) return response.data;

      const { access, refresh, user, membership } = response.data;
      store.setAuth({ accessToken: access, refreshToken: refresh, user, membership });

      navigate(loginDestination(location), { replace: true });
      return response.data;
    },
    [store, navigate, location]
  );

  const completeMfaLogin = useCallback(async ({ challenge, code }) => {
    const { data } = await apiClient.post('/api/v1/auth/login/mfa/', { challenge, code }, { skipAuth: true });
    store.setAuth({ accessToken: data.access, refreshToken: data.refresh, user: data.user, membership: data.membership });
    navigate(loginDestination(location), { replace: true });
    return data;
  }, [store, navigate, location]);

  const logout = useCallback(() => {
    const refresh = store.refreshToken;
    if (refresh && typeof fetch === 'function') {
      // No Authorization header and no refresh interceptor on the logout path.
      fetch(`${BASE_URL}/api/v1/auth/logout/`, {
        method: 'POST', credentials: 'omit', keepalive: true,
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ refresh }),
      }).catch(() => {});
    }
    store.logout();
    navigate('/login', { replace: true });
  }, [store, navigate]);

  const switchTenant = useCallback(
    async (tenantSlug) => {
      const response = await apiClient.post('/api/v1/auth/tenant-switch/', {
        tenant_slug: tenantSlug,
      }, { currentRefresh: true });
      const { access, refresh, membership } = response.data;
      store.updateTokens({ accessToken: access, refreshToken: refresh });
      store.setActiveMembership(membership);
    },
    [store]
  );

  return {
    user: store.user,
    activeMembership: store.activeMembership,
    memberships: store.memberships,
    isAuthenticated: store.isAuthenticated,
    login,
    completeMfaLogin,
    logout,
    switchTenant,
  };
}

/**
 * Loads the user's tenants and the active tenant's features into the auth
 * store whenever the console mounts; ProtectedRoute remounts it for every new
 * principal.
 */
export function useLoadMemberships() {
  useQuery({
    queryKey: ['auth-me'],
    queryFn: async () => {
      const principal = selectPrincipal(useAuthStore.getState());
      const { data } = await apiClient.get('/api/v1/auth/me/');
      // A late answer must never reach the next user's or tenant's session.
      if (selectPrincipal(useAuthStore.getState()) === principal) {
        useAuthStore.getState().updateUser({ id: data.id, name: data.name, email: data.email });
        useAuthStore.getState().setMemberships(data.memberships);
        useAuthStore.getState().setFeatures(principal, data.features ?? []);
      }
      return data;
    },
    // Even from cache: a tenant joined outside the console (an accepted
    // invite) has to show when the console mounts again.
    refetchOnMount: 'always',
  });
}
